import React, { useState, useRef, useEffect } from 'react';
import { NamedLink } from '../../components';

// Workshop secondary bar: a search box (visual stub for v1) + category chips.
//
// Two layouts, chosen by CSS breakpoint:
//   DESKTOP: first 6 chips + a "More" dropdown for the rest (avoids the chips
//            wrapping onto multiple lines, which Jordan disliked). The dropdown
//            works fine on desktop (no overflow-clip issue there).
//   MOBILE:  all 11 chips in one horizontally-scrollable strip (looks clean,
//            and sidesteps the overflow-x:auto dropdown-clipping bug entirely).
//
// Search is intentionally non-functional for v1 (keyword search route does not
// exist yet); submitting just scrolls to the category grid so it never looks
// broken.
const WorkshopSubnav = ({ categories = [], css }) => {
  const all = categories || [];
  const primary = all.slice(0, 6);
  const rest = all.slice(6);

  const [open, setOpen] = useState(false);
  const moreRef = useRef(null);

  // Close the desktop dropdown on outside-click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => { if (moreRef.current && !moreRef.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onSubmit = e => {
    e.preventDefault();
    if (typeof document !== 'undefined') {
      const el = document.getElementById('categories');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const chip = c => (
    <NamedLink
      key={c.slug}
      name="WorkshopCategoryPage"
      params={{ catSlug: c.slug }}
      className={css.chip}
    >
      {c.name}
    </NamedLink>
  );

  return (
    <div className={css.subnav}>
      <div className={css.subnavInner}>
        <form className={css.searchForm} onSubmit={onSubmit} role="search">
          <span className={css.searchIco} aria-hidden="true">⌕</span>
          <input
            type="text"
            className={css.searchInput}
            placeholder="Search trailer parts, gear, and upgrades"
            aria-label="Search trailer parts, gear, and upgrades"
          />
          <button type="submit" className={css.searchBtn}>
            Search
          </button>
        </form>

        {all.length > 0 ? (
          <>
            {/* DESKTOP: 6 chips + More dropdown */}
            <nav className={`${css.chips} ${css.chipsDesktop}`} aria-label="Gear categories">
              {primary.map(chip)}
              {rest.length > 0 ? (
                <div className={css.moreWrap} ref={moreRef}>
                  <button
                    type="button"
                    className={`${css.chip} ${css.chipMore}`}
                    aria-expanded={open}
                    aria-haspopup="true"
                    onClick={() => setOpen(o => !o)}
                  >
                    More <span className={css.moreCaret} aria-hidden="true">▾</span>
                  </button>
                  {open ? (
                    <div className={css.moreMenu} role="menu">
                      {rest.map(c => (
                        <NamedLink
                          key={c.slug}
                          name="WorkshopCategoryPage"
                          params={{ catSlug: c.slug }}
                          className={css.moreItem}
                          role="menuitem"
                          onClick={() => setOpen(false)}
                        >
                          {c.name}
                        </NamedLink>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </nav>

            {/* MOBILE: all chips, horizontal scroll */}
            <nav className={`${css.chips} ${css.chipsMobile}`} aria-label="Gear categories">
              {all.map(chip)}
            </nav>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default WorkshopSubnav;
